import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { Attachment, FeedbackDialogContext, FeedbackFormData } from '~/components/ErrorBoundary/context'
import { produce } from 'immer'
import { Dialog } from '~/ui/Dialog'
import { useListData } from 'react-stately'
import { guid } from '~/lib/utils'

type FeedbackFormInputState = Omit<FeedbackFormData, 'files'>

const initialFormData: FeedbackFormInputState = { facilityId: null, message: '' }

export const FeedbackDialogProvider = ({ children }: PropsWithChildren) => {
  const [feedbackForm, setFeedbackForm] = useState<FeedbackFormInputState>(initialFormData)
  const files = useListData<Attachment>({
    initialItems: [],
    getKey: item => item.id,
  })

  const setFormData = useCallback((updates: Partial<FeedbackFormInputState>) => {
    setFeedbackForm(
      produce(draft => {
        Object.assign(draft, updates)
      })
    )
  }, [])

  const addFiles = useCallback(
    (inputFiles: File[]) => files.append(...inputFiles.map(file => ({ file, id: guid() }))),
    [files]
  )

  const deleteFile = useCallback((id: string) => files.remove(id), [files])

  const resetFormData = useCallback(() => setFeedbackForm(initialFormData), [])

  const value = useMemo(
    () => ({
      ...feedbackForm,
      files: files.items,
      setFormData,
      addFiles,
      deleteFile,
      resetFormData,
    }),
    [feedbackForm, setFormData, resetFormData, files]
  )

  return (
    <FeedbackDialogContext.Provider value={value}>
      <Dialog>{children}</Dialog>
    </FeedbackDialogContext.Provider>
  )
}
